import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { message } from 'antd'
import axiosInstance from '@/lib/axios'

export interface Workshop {
  id: number
  nome: string
  tipo: 'meccanica' | 'carrozzeria'
  responsabile_id: number | null
  indirizzo: string | null
  email: string | null
  telefono1: string | null
  telefono2: string | null
  telefono3: string | null
  esterna: boolean
  attivo: boolean
  user_count: number
  created_at: string | null
  updated_at: string | null
}

export interface WorkshopCreate {
  nome: string
  tipo: 'meccanica' | 'carrozzeria'
  responsabile_id?: number | null
  indirizzo?: string | null
  email?: string | null
  telefono1?: string | null
  telefono2?: string | null
  telefono3?: string | null
  esterna?: boolean
  attivo?: boolean
}

const API = '/workshops'

export const useWorkshops = (attivo?: boolean, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['workshops', attivo],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (attivo !== undefined) params.append('attivo', String(attivo))
      const response = await axiosInstance.get<{ items: Workshop[]; total: number }>(
        `${API}?${params}`
      )
      return response.data
    },
    staleTime: 30_000,
    enabled: options?.enabled ?? true,
  })
}

export const useCreateWorkshop = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: WorkshopCreate) => {
      const response = await axiosInstance.post<Workshop>(API, data)
      return response.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workshops'] })
      message.success('Officina creata')
    },
    onError: () => message.error('Errore nella creazione'),
  })
}

export const useUpdateWorkshop = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<WorkshopCreate> }) => {
      const response = await axiosInstance.put<Workshop>(`${API}/${id}`, data)
      return response.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workshops'] })
      message.success('Officina aggiornata')
    },
    onError: () => message.error('Errore nell\'aggiornamento'),
  })
}

export const useDeleteWorkshop = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await axiosInstance.delete(`${API}/${id}`)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workshops'] })
      message.success('Officina disattivata')
    },
    onError: () => message.error('Errore nella disattivazione'),
  })
}

export const useAssignInterventionWorkshop = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      workOrderId,
      interventionId,
      workshopId,
    }: {
      workOrderId: number
      interventionId: number
      workshopId: number | null
    }) => {
      const response = await axiosInstance.patch(
        `/work-orders/${workOrderId}/interventions/${interventionId}/assign-workshop`,
        { workshop_id: workshopId }
      )
      return response.data
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['work-order', vars.workOrderId] })
      message.success('Officina assegnata')
    },
    onError: () => message.error('Errore nell\'assegnazione'),
  })
}
